/**
 * Speaker Embedding - stores 192-dimensional voice embedding for a user or non-user speaker
 */
export class SpeakerEmbedding {
    id: string;
    userId?: string; // Optional for non-user speakers
    userName?: string;
    userEmail?: string;
    organizationId: string;
    embedding?: number[]; // 192-dimensional array (not usually sent to client)
    audioFileUrl: string;
    enrolledAt: Date;
    isActive: boolean;

    // Non-user speaker fields
    speakerIdentifier?: string;
    displayName?: string;
    customMetadata?: Record<string, string>;

    constructor(data: Partial<SpeakerEmbedding>) {
        this.id = data.id || '';
        this.userId = data.userId;
        this.userName = data.userName;
        this.userEmail = data.userEmail;
        this.organizationId = data.organizationId || '';
        this.embedding = data.embedding;
        this.audioFileUrl = data.audioFileUrl || '';
        this.enrolledAt = data.enrolledAt ? new Date(data.enrolledAt) : new Date();
        this.isActive = data.isActive ?? true;
        this.speakerIdentifier = data.speakerIdentifier;
        this.displayName = data.displayName;
        this.customMetadata = data.customMetadata;
    }

    /**
     * Check if this is a user-based speaker
     */
    get isUserSpeaker(): boolean {
        return !!this.userId;
    }

    /**
     * Get display name for the speaker
     */
    get speakerName(): string {
        if (this.isUserSpeaker) {
            return this.userName || 'Unknown User';
        }
        return this.displayName || this.speakerIdentifier || 'Unknown Speaker';
    }

    /**
     * Get speaker type for display
     */
    get speakerType(): 'User' | 'Custom' {
        return this.isUserSpeaker ? 'User' : 'Custom';
    }
}

/**
 * Speaker Match - individual match result with similarity score
 */
export class SpeakerMatch {
    embeddingId: string;
    userId?: string;
    userName?: string;
    userEmail?: string;
    speakerIdentifier?: string;
    displayName?: string;
    similarityScore: number;
    isConfident: boolean;

    constructor(data: Partial<SpeakerMatch>) {
        this.embeddingId = data.embeddingId || '';
        this.userId = data.userId;
        this.userName = data.userName;
        this.userEmail = data.userEmail;
        this.speakerIdentifier = data.speakerIdentifier;
        this.displayName = data.displayName;
        this.similarityScore = data.similarityScore || 0;
        this.isConfident = data.isConfident ?? false;
    }

    /**
     * Get display name for the matched speaker
     */
    get matchedSpeakerName(): string {
        if (this.userId) {
            return this.userName || 'Unknown User';
        }
        return this.displayName || this.speakerIdentifier || 'Unknown Speaker';
    }

    /**
     * Check if this is a user-based match
     */
    get isUserMatch(): boolean {
        return !!this.userId;
    }

    /**
     * Get color class based on similarity score
     */
    get scoreColor(): string {
        if (this.similarityScore >= 0.25) return 'text-success';
        if (this.similarityScore >= 0.15) return 'text-warning';
        return 'text-error';
    }

    /**
     * Get badge class based on confidence
     */
    get badgeClass(): string {
        return this.isConfident ? 'badge-success' : 'badge-warning';
    }
}

/**
 * Speaker Identification Result - result of identifying a speaker
 */
export class SpeakerIdentificationResult {
    id: string;
    labelId: string;
    organizationId: string;
    identifiedEmbeddingId?: string;
    identifiedUserId?: string;
    identifiedSpeakerName?: string;
    identifiedSpeakerIdentifier?: string;
    similarityScore: number;
    threshold: number;
    isConfident: boolean;
    createdAt: Date;

    constructor(data: Partial<SpeakerIdentificationResult>) {
        this.id = data.id || '';
        this.labelId = data.labelId || '';
        this.organizationId = data.organizationId || '';
        this.identifiedEmbeddingId = data.identifiedEmbeddingId;
        this.identifiedUserId = data.identifiedUserId;
        this.identifiedSpeakerName = data.identifiedSpeakerName;
        this.identifiedSpeakerIdentifier = data.identifiedSpeakerIdentifier;
        this.similarityScore = data.similarityScore || 0;
        this.threshold = data.threshold || 0.25;
        this.isConfident = data.isConfident ?? false;
        this.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    }

    /**
     * Check if this is a user-based identification
     */
    get isUserIdentification(): boolean {
        return !!this.identifiedUserId;
    }

    /**
     * Get status text for display
     */
    get statusText(): string {
        if (this.isConfident && this.identifiedSpeakerName) {
            return `Identified: ${this.identifiedSpeakerName}`;
        }
        return 'Unknown Speaker';
    }

    /**
     * Get color class based on confidence
     */
    get statusColor(): string {
        return this.isConfident ? 'text-success' : 'text-warning';
    }

    /**
     * Format similarity score as percentage
     */
    get similarityPercentage(): string {
        return `${(this.similarityScore * 100).toFixed(1)}%`;
    }
}

/**
 * DTOs for API requests
 */
export interface EnrollSpeakerRequest {
    // User-based enrollment
    userId?: string;

    // Non-user enrollment
    speakerIdentifier?: string;
    displayName?: string;
    customMetadata?: Record<string, string>;

    // Common fields
    audioFileUrl: string;
}

export interface EnrollSpeakerResponse {
    embeddingId?: string;
    success: boolean;
    message: string;
}

export interface IdentifySpeakerRequest {
    labelId: string;
    audioFileUrl: string;
}

export interface IdentifySpeakerResponse {
    identifiedEmbeddingId?: string;
    identifiedUserId?: string;
    identifiedSpeakerName?: string;
    identifiedSpeakerIdentifier?: string;
    similarityScore: number;
    threshold: number;
    isConfident: boolean;
    message: string;
    topMatches: SpeakerMatch[];
}
